import React from 'react';
import { Box, Typography, Checkbox } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import {
  CheckCircle,
  Assignment,
  Settings,
  Event,
  Payment,
  People,
  SupervisorAccount,
  Person,
  ShortText,
  Notes,
  CalendarToday,
  ArrowDropDownCircle,
  RadioButtonChecked,
  CheckBox,
  AttachFile,
  Create,
  LocationOn,
  Call,
  Email,
  AssignmentTurnedIn,
  LiveHelp,
} from '@material-ui/icons';
import { TasksAnalyseResponse } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    resultsContainer: {
      maxHeight: '450px',
      overflow: 'auto',
    },
    summaryBox: {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    resultSection: {
      marginBottom: theme.spacing(3),
    },
    resultSectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.primary,
    },
    sectionIcon: {
      color: theme.palette.text.secondary,
      fontSize: 22,
      marginRight: theme.spacing(1.5),
    },
    taskCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
    },
    taskCardHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(0.5),
    },
    taskCheckbox: {
      padding: 0,
      marginRight: theme.spacing(1.5),
    },
    taskIcon: {
      color: theme.palette.text.secondary,
      fontSize: 20,
      marginRight: theme.spacing(1),
    },
    taskName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginRight: theme.spacing(1),
      fontSize: '0.875rem',
    },
    taskDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginLeft: theme.spacing(6.5),
    },
    settingCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      alignItems: 'flex-start',
      cursor: 'pointer',
    },
    settingCheckbox: {
      padding: 0,
      marginRight: theme.spacing(1.5),
    },
  })
);

const getTaskTypeIcon = (taskType: string) => {
  switch (taskType) {
    case 'event':
      return Event;
    case 'referee':
      return People;
    case 'manager':
      return SupervisorAccount;
    case 'payment':
      return Payment;
    default:
      return Assignment; // User Completes
  }
};

const getTaskTypeLabel = (taskType: string) => {
  switch (taskType) {
    case 'event':
      return 'Event';
    case 'referee':
      return 'Referee Check';
    case 'manager':
      return 'Manager Completes';
    case 'payment':
      return 'Payment';
    default:
      return 'User Completes';
  }
};

// Get unique ID for a task (libraryId for library tasks, name for custom tasks)
const getTaskId = (task: { libraryId: string | null; name: string }) => task.libraryId ?? task.name;

const getFieldTypeIcon = (fieldType: string) => {
  switch (fieldType.toUpperCase()) {
    case 'SHORT':
      return ShortText;
    case 'LONG':
      return Notes;
    case 'PHONE':
      return Call;
    case 'EMAIL':
      return Email;
    case 'ADDRESS':
      return LocationOn;
    case 'DATE':
      return CalendarToday;
    case 'NUMBER':
      return ShortText;
    case 'DROPDOWN':
      return ArrowDropDownCircle;
    case 'MULTIPLE_CHOICE':
      return RadioButtonChecked;
    case 'CHECKBOX':
      return CheckBox;
    case 'ATTACHMENT':
      return AttachFile;
    case 'SIGNATURE':
      return Create;
    case 'QUIZ':
      return AssignmentTurnedIn;
    default:
      return ShortText;
  }
};

const getFieldTypeLabel = (fieldType: string) => {
  switch (fieldType.toUpperCase()) {
    case 'SHORT':
      return 'Short Text';
    case 'LONG':
      return 'Long Text';
    case 'PHONE':
      return 'Phone';
    case 'EMAIL':
      return 'Email';
    case 'ADDRESS':
      return 'Address';
    case 'DATE':
      return 'Date';
    case 'NUMBER':
      return 'Number';
    case 'DROPDOWN':
      return 'Dropdown';
    case 'MULTIPLE_CHOICE':
      return 'Multiple Choice';
    case 'CHECKBOX':
      return 'Checkbox';
    case 'ATTACHMENT':
      return 'Attachment';
    case 'SIGNATURE':
      return 'Signature';
    case 'QUIZ':
      return 'Quiz';
    default:
      return fieldType;
  }
};

interface TasksResultsViewProps {
  results: TasksAnalyseResponse;
  selectedTasks: Set<string>;
  selectedSettings: Set<string>;
  selectedProfileFields: Set<string>;
  selectedScreeningQuestions: Set<string>;
  onToggleTask: (taskId: string) => void;
  onToggleSetting: (settingId: string) => void;
  onToggleProfileField: (fieldName: string) => void;
  onToggleScreeningQuestion: (label: string) => void;
}

export function TasksResultsView({
  results,
  selectedTasks,
  selectedSettings,
  selectedProfileFields,
  selectedScreeningQuestions,
  onToggleTask,
  onToggleSetting,
  onToggleProfileField,
  onToggleScreeningQuestion,
}: TasksResultsViewProps) {
  const classes = useStyles();

  return (
    <Box className={classes.resultsContainer}>
      {/* Summary */}
      <Box className={classes.summaryBox}>
        <Box display='flex' alignItems='center' mb={1}>
          <CheckCircle style={{ marginRight: 12, color: '#4caf50' }} />
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
            Analysis Complete
          </Typography>
        </Box>
        <Typography variant='body2' color='textSecondary'>
          {results.analysis.summary}
        </Typography>
      </Box>

      {/* Tasks */}
      {results.tasks && results.tasks.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <Assignment className={classes.sectionIcon} />
            Tasks to Add ({selectedTasks.size} of {results.tasks.length} selected)
          </Typography>
          {results.tasks.map((task) => {
            const IconComponent = getTaskTypeIcon(task.taskType);
            const taskId = getTaskId(task);
            const isCustomTask = task.libraryId === null;
            return (
              <Box
                key={taskId}
                className={classes.taskCard}
                onClick={() => onToggleTask(taskId)}
                style={{ opacity: selectedTasks.has(taskId) ? 1 : 0.6 }}
              >
                <Box className={classes.taskCardHeader}>
                  <Checkbox
                    checked={selectedTasks.has(taskId)}
                    color='primary'
                    size='small'
                    className={classes.taskCheckbox}
                  />
                  <IconComponent className={classes.taskIcon} />
                  <Typography className={classes.taskName}>{task.name}</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {getTaskTypeLabel(task.taskType)}
                    {isCustomTask && ' (Custom)'}
                  </Typography>
                </Box>
                <Typography className={classes.taskDescription}>{task.description}</Typography>
                {isCustomTask && task.fields && task.fields.length > 0 && (
                  <Typography className={classes.taskDescription} style={{ fontSize: '0.75rem', marginTop: 4 }}>
                    Fields: {task.fields.map((f) => f.name).join(', ')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Settings */}
      {results.settings && results.settings.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <Settings className={classes.sectionIcon} />
            Settings to Enable ({selectedSettings.size} of {results.settings.length} selected)
          </Typography>
          {results.settings.map((setting) => (
            <Box
              key={setting.settingId}
              className={classes.settingCard}
              onClick={() => onToggleSetting(setting.settingId)}
              style={{ opacity: selectedSettings.has(setting.settingId) ? 1 : 0.6 }}
            >
              <Checkbox
                checked={selectedSettings.has(setting.settingId)}
                color='primary'
                size='small'
                className={classes.settingCheckbox}
              />
              <Box flex={1}>
                <Typography variant='body2' style={{ fontWeight: 600 }}>
                  {setting.name}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {setting.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Profile Fields */}
      {results.profileFields && results.profileFields.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <Person className={classes.sectionIcon} />
            Profile Fields ({selectedProfileFields.size} of {results.profileFields.length} selected)
          </Typography>
          {results.profileFields.map((field) => {
            const FieldIcon = getFieldTypeIcon(field.type);
            return (
              <Box
                key={field.name}
                className={classes.taskCard}
                onClick={() => onToggleProfileField(field.name)}
                style={{ opacity: selectedProfileFields.has(field.name) ? 1 : 0.6 }}
              >
                <Box className={classes.taskCardHeader}>
                  <Checkbox
                    checked={selectedProfileFields.has(field.name)}
                    color='primary'
                    size='small'
                    className={classes.taskCheckbox}
                  />
                  <FieldIcon className={classes.taskIcon} />
                  <Typography className={classes.taskName}>{field.name}</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {getFieldTypeLabel(field.type)}
                  </Typography>
                </Box>
                <Typography className={classes.taskDescription}>{field.description}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Screening Questions */}
      {results.screeningQuestions && results.screeningQuestions.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <LiveHelp className={classes.sectionIcon} />
            Screening Questions ({selectedScreeningQuestions.size} of {results.screeningQuestions.length} selected)
          </Typography>
          {results.screeningQuestions.map((question) => (
            <Box
              key={question.label}
              className={classes.taskCard}
              onClick={() => onToggleScreeningQuestion(question.label)}
              style={{ opacity: selectedScreeningQuestions.has(question.label) ? 1 : 0.6 }}
            >
              <Box className={classes.taskCardHeader}>
                <Checkbox
                  checked={selectedScreeningQuestions.has(question.label)}
                  color='primary'
                  size='small'
                  className={classes.taskCheckbox}
                />
                <LiveHelp className={classes.taskIcon} />
                <Typography className={classes.taskName}>{question.label}</Typography>
                <Typography variant='body2' color='textSecondary'>
                  {question.correctAnswer === 'yes'
                    ? 'Must answer Yes'
                    : question.correctAnswer === 'no'
                    ? 'Must answer No'
                    : 'Either answer'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Recommendations */}
      {results.recommendations && results.recommendations.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>Recommendations</Typography>
          <Box component='ul' style={{ margin: 0, paddingLeft: 20 }}>
            {results.recommendations.map((rec) => (
              <li key={rec}>
                <Typography variant='body2' color='textSecondary'>
                  {rec}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Helper to get initial selections from response
export function getInitialTaskSelections(response: TasksAnalyseResponse) {
  return {
    tasks: new Set((response.tasks || []).filter((t) => t.recommended !== false).map((t) => getTaskId(t))),
    settings: new Set((response.settings || []).filter((s) => s.recommended !== false).map((s) => s.settingId)),
    profileFields: new Set((response.profileFields || []).map((f) => f.name)),
    screeningQuestions: new Set((response.screeningQuestions || []).map((q) => q.label)),
  };
}

// Helper to build apply summary
export function getTasksApplySummary(
  selectedTasks: Set<string>,
  selectedSettings: Set<string>,
  selectedProfileFields: Set<string>,
  selectedScreeningQuestions: Set<string>
): string {
  const parts: string[] = [];
  if (selectedTasks.size > 0) {
    parts.push(`${selectedTasks.size} task${selectedTasks.size !== 1 ? 's' : ''} created`);
  }
  if (selectedSettings.size > 0) {
    parts.push(`${selectedSettings.size} setting${selectedSettings.size !== 1 ? 's' : ''} enabled`);
  }
  if (selectedProfileFields.size > 0) {
    parts.push(`${selectedProfileFields.size} profile field${selectedProfileFields.size !== 1 ? 's' : ''} added`);
  }
  if (selectedScreeningQuestions.size > 0) {
    parts.push(
      `${selectedScreeningQuestions.size} screening question${selectedScreeningQuestions.size !== 1 ? 's' : ''} added`
    );
  }
  return parts.length > 0 ? parts.join(', ') : 'Configuration applied';
}
